
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

import { RouteName } from '@/router/routes';
import { ItemPanier } from '@/store/modules/panier';
import { buildUrlParamIdLibelle } from '@/router/utils';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { getQuantiteUnite } from '@/utils/conditionnement';
import { CommandeApi } from '@/api/commande';
import { mapPanierToCommande } from './panier-commande.mapper';
import { cloneDeep } from 'lodash';
import { ConfirmationCommande } from '@/model/confirmation-commande';
import { isSameDay } from 'date-fns';

export default Vue.extend({
  name: 'SynthesePanier',
  data () {
    return {
      loading: false,
      loadingPrix: true,
      items: [] as ItemPanier[],
      zoneCommentaireVisible: false,
      commentaire: null,
      confirmationCommande: null as ConfirmationCommande,
      showSuccessDialog: false
    };
  },
  computed: {
    ...mapGetters({
      username: 'user/username',
      isTiers: 'user/isTiers',
      itemsOfPanier: 'panier/items',
      articlesLieuxDepart: 'articlesLieuxDepart/liste',
      commentaireSaisie: 'panier/commentaireSaisie'
    }),
    montantTotalHT (): number {
      return (this.items as ItemPanier[]).reduce((total, ligne) => total + this.getMontantHT(ligne), 0);
    },
    montantTotalTTC (): number {
      return (this.items as ItemPanier[]).reduce((total, ligne) => total + this.getMontantTTC(ligne), 0);
    }
  },
  watch: {
    itemsOfPanier: {
      handler () {
        this.updatePrix();
      },
      immediate: true
    }
  },
  created () {
    this.items = this.itemsOfPanier.slice();
    this.updatePrix();

    this.commentaire = this.commentaireSaisie.commentaire;
    if (this.commentaire != null) {
      this.zoneCommentaireVisible = true;
    }
  },
  methods: {
    ...mapActions({
      viderPanier: 'panier/viderPanier',
      suprimerItemPanier: 'panier/suprimerItemPanier',
      modifierItemPanier: 'panier/modifierItemPanier',
      clearCacheArticles: 'article/clearCache',
      setCommentaireSaisie: 'panier/setCommentaireSaisie'
    }),
    updatePrix () {
      if (this.itemsOfPanier.length === 0) {
        this.items = [];

        if (!(this.items && this.items.length)) {
          this.setCommentaireSaisie({ commentaire: '' });
        }

        return;
      }

      this.loadingPrix = true;
      const commande = mapPanierToCommande(this.username, this.itemsOfPanier);

      return CommandeApi.valoriserCommande(commande)
        .then(commandeValorisee => {
          this.items = this.itemsOfPanier.slice();

          for (const item of this.items as ItemPanier[]) {
            const ligne = commandeValorisee.lignes.find(ligne => item.idLigne === ligne.idLigne);
            if (ligne) {
              item.prixUnitaireHT = ligne.reglement.prixUnitaireHT;
              item.prixUnitaireTTC = ligne.reglement.prixUnitaireTTC;
            }
          }
        }).catch((err) => {
          this.manageError(err);
        }).finally(() => {
          this.loadingPrix = false;
        });
    },
    validerPanier () {
      if (this.loadingPrix) {
        return;
      }

      this.$dialog.confirm({
        text: '<b>Confirmation définitive de votre commande</b><div style="border: 2px solid #bdc3c7; padding: 5px; border-radius: 5px"><strong>Rappel: </strong> Votre document "Pr&eacute;curseurs explosifs" doit &ecirc;tre &agrave; jour au moment de la livraison. Il pourra vous &ecirc;tre demand&eacute;.</div>',
        actions: {
          false: {
            text: 'Annuler',
            flat: false,
            outlined: true,
            color: 'grey'
          },
          true: {
            text: 'Confirmer définitivement',
            color: 'primary',
            flat: false

          }
        }
      }).then(confirm => {
        if (confirm) {
          const commande = mapPanierToCommande(this.username, this.items);

          this.loading = true;

          CommandeApi.creerCommande(commande)
            .then((confirmationCommande) => {
              this.confirmationCommande = confirmationCommande;
              this.viderPanier();
              this.showSuccessDialog = true;
            }).catch(err => {
              this.manageError(err);
            }).finally(() => {
              this.loading = false;
            });
        }
      });
    },
    updateLignePanierAfterError (dateDebutCotation: Date, dateFinCotation: Date, ancienPrix: number, nouveauPrix: number) {
      const item: ItemPanier = this.itemsOfPanier.find((i: ItemPanier) => i.prixUnitaireHT === ancienPrix && isSameDay(i.echeanceLivraison, dateDebutCotation));
      if (item) {
        this.loadingPrix = true;
        const newItem = cloneDeep(item);
        newItem.prixUnitaireHT = nouveauPrix;
        this.modifierItemPanier(newItem).then(() => {
          this.updatePrix();
        }).finally(() => {
          this.loadingPrix = false;
          this.$forceUpdate();
        });
      }
    },
    manageError (err: any) {
      if (err.response.status === 409) {
        if (err.response.data?.code === 'COMMANDE_ERREURS') {
          let textHtml = '<p>Attention, pour les articles suivants, plusieurs erreurs ont été détectées : </p><div style="display:flex;justify-content: center;"><ul style="text-align:left">';

          const formatPrix = (value: number) => this.$options.filters.decimal(value, '.2') + ' €/t';

          const globalErrors = (err.response.data.message.match(/\[(?<liste>.*)\]/).groups.liste as string).split(';');
          globalErrors.forEach(error => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [code, message, numArticle, libelleArticle, commentaireArticle, libelleLieuDepart, dateDebutStr, dateFinStr, ...data] = error.split(':');

            const dateDebut = toDate(dateDebutStr, { timeZone: 'Europe/Paris' });
            const dateFin = toDate(dateFinStr, { timeZone: 'Europe/Paris' });

            const designationArticle = `<strong>${libelleArticle} ${commentaireArticle ? '(' + commentaireArticle + ')' : ''}</strong>`;

            switch (code) {
              case 'COMMANDE_PRIX_MODIFIE':
                // eslint-disable-next-line no-case-declarations
                const [ancienPrix, nouveauPrix] = data.map(parseFloat);
                textHtml += `<li>La cotation du ${designationArticle} a changé depuis votre dernière saisie :`;
                textHtml += `<ul>
                              <li>Ancien prix : ${formatPrix(ancienPrix)}</li>
                              <li>Nouveau prix : ${formatPrix(nouveauPrix)}</li>
                            </ul></li><br/>`;
                this.updateLignePanierAfterError(dateDebut, dateFin, ancienPrix, nouveauPrix);
                break;

              default:
                textHtml += `<li>${designationArticle} : ${message}</li><br/>`;
                break;
            }
          });

          this.$dialog.warning({
            title: 'Attention',
            text: textHtml,
            actions: {
              0: {
                text: 'J\'ai compris',
                flat: false,
                outlined: true,
                color: 'warning'
              }
            }
          });
        }
      } else {
        this.$dialog.error({
          title: 'Une erreur est survenue',
          text: 'Une erreur est survenue.<br/>Veuillez rééssayer plus tard.',
          actions: {
            0: {
              text: 'J\'ai compris',
              flat: false,
              outlined: true
            }
          }
        });
      }
    },
    closeAndGoToDashboard () {
      this.$router.push({ name: RouteName.MAIN_DASHBOARD });
    },
    modifierLigne (ligne: ItemPanier) {
      this.$router.push({ name: RouteName.MAIN_PANIER_MODIF, params: { idLignePanier: ligne.idLigne + '', idArticle: buildUrlParamIdLibelle(ligne.articleLieuDepart.article) } });
    },
    supprimerLigne (ligne: ItemPanier) {
      this.$dialog.confirm({
        title: 'Suppression',
        text: 'Voulez-vous vraiment supprimer cet article du panier ?'
      }).then((confirm) => {
        if (confirm) {
          this.suprimerItemPanier(ligne.idLigne);
        }
      });
    },
    getQuantiteTonne (ligne: ItemPanier): number {
      return getQuantiteUnite(ligne.quantite, ligne.conditionnement, 't');
    },
    getMontantHT (ligne: ItemPanier): number {
      return ligne.prixUnitaireHT * this.getQuantiteTonne(ligne);
    },
    getMontantTTC (ligne: ItemPanier): number {
      return ligne.prixUnitaireTTC * this.getQuantiteTonne(ligne);
    },
    formatDate (date: Date, format: string): string {
      return formatInTimeZone(date, 'Europe/Paris', format);
    },
    getAdresseText (ligne: ItemPanier) {
      const adr1 = [ligne.adresse.ligneAdresse1?.trim(), ligne.adresse.ligneAdresse2?.trim(), ligne.adresse.ligneAdresse3?.trim()].filter(Boolean).join(', ');
      const adr2 = ligne.adresse.codePostal + ' ' + ligne.adresse.ville;

      return adr1 + ' - ' + adr2;
    },
    toggleDetail (ligne: ItemPanier) {
      Object.defineProperty(ligne, 'showDetail', { value: !(ligne as any).showDetail, enumerable: false, configurable: true });
      this.$forceUpdate();
    },
    afficherZoneCommentaire () {
      this.zoneCommentaireVisible = true;
    },
    sauvegardeCommentaire () {
      this.setCommentaireSaisie({ commentaire: this.commentaire });
    }
  }
});
